import { MobileSearchInput } from '@app/features/command/mobile/MobileSearchInput';
import { SearchState } from '@app/features/command/mobile/mobileSearchState';
import { useFeatureFlag } from '@app/lib/analytics/posthog';
import {
  enableCalendarScheduling,
  enableTasksReviews,
} from '@core/constant/featureFlags';
import { useSettingsState } from '@core/constant/SettingsState';
import { triggerFocusInput } from '@core/directive/focusInput';
import { hapticImpact } from '@core/mobile/haptics';
import CalendarCheckIcon from '@phosphor/calendar-check.svg';
import CaretUpIcon from '@phosphor/caret-up.svg';
import IconGear from '@phosphor/gear.svg';
import GitPullRequestIcon from '@phosphor/git-pull-request.svg';
import SearchIcon from '@phosphor/magnifying-glass.svg';
import { createElementSize } from '@solid-primitives/resize-observer';
import { cn } from '@ui';
import { createSignal, For, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { MobileDockButton } from './MobileDockButton';
import { MobileDockIsland } from './MobileDockIsland';
import { MobileDrawer } from './MobileDrawer';
import { MobileBottomEdgeFade } from './MobileEdgeFade';
import { type MobileDockView, useMobileDockViews } from './mobile-dock-views';
import {
  type MobileDockNavId,
  useForegroundMobileView,
  useMobileNavNavigate,
} from './use-mobile-nav';

function MoreViewsDrawer(props: {
  views: readonly MobileDockView[];
  isActive: (id: MobileDockNavId) => boolean;
  onNavigate: (id: MobileDockNavId) => void;
}) {
  const { settingsOpen, toggleSettings } = useSettingsState();
  const reviewsFlag = useFeatureFlag(enableTasksReviews);
  const schedulingFlag = useFeatureFlag(enableCalendarScheduling);

  return (
    <MobileDrawer
      side="bottom"
      preventScroll={false}
      preventScrollbarShift={false}
      closeOnOutsidePointerStrategy="pointerdown"
    >
      <MobileDrawer.Trigger
        as={MobileDockButton}
        ariaLabel="More views"
        icon={CaretUpIcon}
      />
      <MobileDrawer.Portal>
        <MobileDrawer.Overlay />
        <MobileDrawer.Content aria-label="More views">
          <MobileDrawer.Handle />
          <MobileDrawer.ScrollBody>
            <div class="mx-3 flex flex-col gap-1 px-1">
              <MobileDrawer.Close
                as={MobileDrawer.Item}
                aria-label="Settings"
                class={settingsOpen() ? 'text-accent' : undefined}
                onClick={() => {
                  hapticImpact('light');
                  toggleSettings();
                }}
              >
                <IconGear class="size-4 shrink-0" />
                <span>Settings</span>
              </MobileDrawer.Close>
              <Show
                when={
                  props.views.length > 0 ||
                  reviewsFlag().enabled ||
                  schedulingFlag().enabled
                }
              >
                <div class="-mx-1 h-px shrink-0 bg-edge" />
              </Show>
              <For each={props.views.toReversed()}>
                {(view) => (
                  <MobileDrawer.Close
                    as={MobileDrawer.Item}
                    aria-label={view.label}
                    class={props.isActive(view.id) ? 'text-accent' : undefined}
                    aria-current={props.isActive(view.id) ? 'page' : undefined}
                    onClick={() => {
                      hapticImpact('light');
                      props.onNavigate(view.id);
                    }}
                  >
                    <Dynamic component={view.icon} class="size-4 shrink-0" />
                    <span>{view.label}</span>
                  </MobileDrawer.Close>
                )}
              </For>
              <Show when={reviewsFlag().enabled}>
                <MobileDrawer.Close
                  as={MobileDrawer.Item}
                  aria-label="Reviews"
                  class={props.isActive('reviews') ? 'text-accent' : undefined}
                  aria-current={props.isActive('reviews') ? 'page' : undefined}
                  onClick={() => {
                    hapticImpact('light');
                    props.onNavigate('reviews');
                  }}
                >
                  <GitPullRequestIcon class="size-4 shrink-0" />
                  <span>Reviews</span>
                </MobileDrawer.Close>
              </Show>
              <Show when={schedulingFlag().enabled}>
                <MobileDrawer.Close
                  as={MobileDrawer.Item}
                  aria-label="Scheduling"
                  class={
                    props.isActive('booking-links') ? 'text-accent' : undefined
                  }
                  aria-current={
                    props.isActive('booking-links') ? 'page' : undefined
                  }
                  onClick={() => {
                    hapticImpact('light');
                    props.onNavigate('booking-links');
                  }}
                >
                  <CalendarCheckIcon class="size-4 shrink-0" />
                  <span>Scheduling</span>
                </MobileDrawer.Close>
              </Show>
              <div class="-mx-1 h-px shrink-0 bg-edge" />
              <MobileDrawer.Close
                aria-label="Views"
                class="flex h-9 shrink-0 items-center px-3 text-sm font-medium text-ink-muted"
                onClick={() => hapticImpact('light')}
              >
                Views
              </MobileDrawer.Close>
            </div>
          </MobileDrawer.ScrollBody>
        </MobileDrawer.Content>
      </MobileDrawer.Portal>
    </MobileDrawer>
  );
}

/**
 * The navigation dock groups as many fixed-size view buttons as fit with More,
 * with Search on its own island.
 * Pressing Search flips the row to the search
 * layout (see MobileDockRow); the current view's button shows in accent.
 */
function MobileNavigationDockRow() {
  const navigate = useMobileNavNavigate();
  const foregroundView = useForegroundMobileView();
  const dockViews = useMobileDockViews();

  const [navRef, setNavRef] = createSignal<HTMLDivElement>();
  const navSize = createElementSize(navRef);
  const visibleCount = () => {
    // The island height and square buttons share the same CSS size. Its width
    // already excludes Search and the gutters; reserve one button for More.
    if (!navSize.height) return 0;
    return Math.max(0, Math.floor(navSize.width / navSize.height) - 1);
  };

  return (
    <div class="flex w-full justify-between gap-(--mobile-chrome-gutter)">
      <MobileDockIsland
        ref={setNavRef}
        class="h-(--mobile-chrome-button-size) min-w-0 flex-1 justify-between"
        style={{
          'max-width': `calc(${dockViews().length + 1} * var(--mobile-chrome-button-size))`,
        }}
      >
        <For each={dockViews().slice(0, visibleCount())}>
          {(button) => (
            <MobileDockButton
              icon={
                foregroundView() === button.id
                  ? (button.iconActive ?? button.icon)
                  : button.icon
              }
              ariaLabel={button.label}
              active={foregroundView() === button.id}
              onClick={() => navigate(button.id)}
            />
          )}
        </For>
        <MoreViewsDrawer
          views={dockViews().slice(visibleCount())}
          isActive={(id) => foregroundView() === id}
          onNavigate={navigate}
        />
      </MobileDockIsland>
      <MobileDockIsland class="shrink-0">
        <MobileDockButton
          icon={SearchIcon}
          ariaLabel="Search"
          onClick={() => {
            // Mount the real input first, then focus it in this same user
            // gesture. Deferring the focus transfer can lose iOS keyboard
            // activation when reopening search.
            SearchState.open();
            triggerFocusInput(() =>
              document.getElementById('mobile-search-input')
            );
            navigate('search');
          }}
        />
      </MobileDockIsland>
    </div>
  );
}

type MobileDockRowProps = {
  class?: string;
};

/**
 * The bottom-most chrome row. By default it is the navigation dock (see
 * MobileNavigationDockRow); pressing its Search button opens a search session,
 * which swaps in the search bar and category pills in the accessory slot
 * above (see MobileViewsRow). Pressing the
 * input's X ends the session and restores the navigation dock.
 */
export function MobileDockRow(props: MobileDockRowProps) {
  return (
    <div
      class={cn(
        'flex items-center gap-(--mobile-chrome-gap) px-(--mobile-chrome-gutter)',
        props.class
      )}
    >
      <MobileBottomEdgeFade />
      <Show when={SearchState.isOpen()} fallback={<MobileNavigationDockRow />}>
        <MobileSearchInput />
      </Show>
    </div>
  );
}
